// src/pages/dashboard/AdminDemoRequests.jsx
import React, { useEffect, useMemo, useState } from "react";
import PageShell from "./PageShell";
import { demoRequestsApi } from "../../core/api/demoRequestsApi";

function toCsv(rows, headers) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = headers.map((h) => esc(h.label)).join(",");
  const body = rows.map((r) => headers.map((h) => esc(r[h.key])).join(",")).join("\n");
  return `${head}\n${body}\n`;
}

function downloadCsv(filename, csvText) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminDemoRequests() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  // ✅ scroll lock when modal open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const statusSupported = useMemo(() => {
    return items?.some((x) => Object.prototype.hasOwnProperty.call(x, "status"));
  }, [items]);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const data = await demoRequestsApi.list();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load demo requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    return items.filter((x) => {
      const hay = `${x.name || ""} ${x.email || ""} ${x.phone || ""} ${x.company || ""}`.toLowerCase();
      if (text && !hay.includes(text)) return false;

      if (status !== "ALL" && statusSupported) {
        if ((x.status || "NEW") !== status) return false;
      }

      const dt = x.submitted_at ? new Date(x.submitted_at) : null;
      if (from) {
        const f = new Date(from);
        if (!dt || dt < f) return false;
      }
      if (to) {
        const t = new Date(to);
        t.setHours(23, 59, 59, 999);
        if (!dt || dt > t) return false;
      }

      return true;
    });
  }, [items, q, status, from, to, statusSupported]);

  const onView = (row) => {
    setActive(row);
    setOpen(true);
  };

  const onDelete = async (row) => {
    const ok = window.confirm("Delete this demo request?");
    if (!ok) return;
    try {
      await demoRequestsApi.remove(row.id);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onUpdateStatus = async (row, newStatus) => {
    if (!statusSupported) {
      alert("Backend status field not available yet. Add status in backend to use this.");
      return;
    }
    try {
      await demoRequestsApi.update(row.id, { status: newStatus });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update status failed");
    }
  };

  const exportCsv = () => {
    const headers = [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "company", label: "Company" },
      { key: "product", label: "Product" },
      { key: "message", label: "Message" },
      { key: "status", label: "Status" },
      { key: "submitted_at", label: "Created At" },
    ];

    const rows = filtered.map((x) => ({
      name: x.name,
      email: x.email,
      phone: x.phone,
      company: x.company,
      product: x.product,
      message: x.message,
      status: statusSupported ? x.status : "NEW",
      submitted_at: x.submitted_at,
    }));

    const csv = toCsv(rows, headers);
    downloadCsv(`demo_requests_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <PageShell title="Demo Requests">
      <div className="mt-4 space-y-4">
        {/* ✅ Filters (REMOVED z-[9999]) */}
        <div className="bg-white rounded-xl shadow p-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between relative z-10">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700">Search</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="name / email / phone / company"
              className="mt-1 w-full border rounded-lg px-3 py-2 outline-none focus:ring"
            />
          </div>

          <div className="w-full md:w-56">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 outline-none focus:ring bg-white"
              disabled={!statusSupported}
              title={!statusSupported ? "Add status field in backend to enable" : ""}
            >
              <option value="ALL">All</option>
              <option value="NEW">NEW</option>
              <option value="CONTACTED">CONTACTED</option>
              <option value="CLOSED">CLOSED</option>
            </select>
          </div>

          <div className="w-full md:w-44">
            <label className="text-sm font-medium text-gray-700">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 outline-none focus:ring"
            />
          </div>

          <div className="w-full md:w-44">
            <label className="text-sm font-medium text-gray-700">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 outline-none focus:ring"
            />
          </div>

          <div className="flex gap-2">
            <button onClick={load} className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:opacity-90">
              Refresh
            </button>
            <button onClick={exportCsv} className="px-4 py-2 rounded-lg border bg-white hover:bg-gray-50">
              Export CSV
            </button>
          </div>
        </div>

        {err ? (
          <div className="bg-red-50 text-red-700 border border-red-100 rounded-lg p-3">{err}</div>
        ) : null}

        {/* Table */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3">Sr No</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-left px-4 py-3">Created At</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td className="px-4 py-4" colSpan={8}>Loading...</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4" colSpan={8}>No records found.</td>
                  </tr>
                ) : (
                  filtered.map((row, idx) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{idx + 1}</td>
                      <td className="px-4 py-3">{row.name}</td>
                      <td className="px-4 py-3">{row.email}</td>
                      <td className="px-4 py-3">{row.phone || "-"}</td>
                      <td className="px-4 py-3">{row.company || "-"}</td>
                      <td className="px-4 py-3">
                        {row.submitted_at ? new Date(row.submitted_at).toLocaleString() : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded-full text-xs border">
                          {statusSupported ? row.status : "NEW"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => onView(row)} className="px-3 py-1 rounded-lg border hover:bg-gray-50">
                            View
                          </button>

                          <select
                            className="px-3 py-1 rounded-lg border bg-white"
                            disabled={!statusSupported}
                            title={!statusSupported ? "Add status in backend" : ""}
                            value={statusSupported ? row.status : "NEW"}
                            onChange={(e) => onUpdateStatus(row, e.target.value)}
                          >
                            <option value="NEW">NEW</option>
                            <option value="CONTACTED">CONTACTED</option>
                            <option value="CLOSED">CLOSED</option>
                          </select>

                          <button
                            onClick={() => onDelete(row)}
                            className="px-3 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ✅ Modal */}
        {open && active ? (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4 z-[99999]"
            onClick={() => setOpen(false)}
          >
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b flex items-center justify-between">
                <div className="font-semibold text-lg">Demo Request Details</div>
                <button onClick={() => setOpen(false)} className="px-3 py-1 rounded-lg border hover:bg-gray-50">
                  Close
                </button>
              </div>

              <div className="p-4 space-y-3 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-gray-600">Name</div>
                    <div className="font-medium">{active.name}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Email</div>
                    <div className="font-medium">{active.email}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Phone</div>
                    <div className="font-medium">{active.phone || "-"}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Company</div>
                    <div className="font-medium">{active.company || "-"}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Product</div>
                    <div className="font-medium">{active.product || "-"}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Submitted At</div>
                    <div className="font-medium">
                      {active.submitted_at ? new Date(active.submitted_at).toLocaleString() : "-"}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-gray-600">Message / Requirement</div>
                  <div className="mt-1 whitespace-pre-wrap border rounded-lg p-3 bg-gray-50">
                    {active.message || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}
